import { SmsTemplateStatus, SmsType } from "@/models/club";
import { PlaceHolder } from "@/services/smsPlaceHolders/type";

// Backend SmsType: 0 SimpleSms, 1 Discount, 2 Region, 4 the automatic SMS after a booking. Labels match the flows' names.
export const smsTypeLabels: Record<number, string> = {
  [SmsType.Value0]: "پیامک به مشتریان",
  [SmsType.Value1]: "پیامک تخفیف",
  [SmsType.Value2]: "پیامک منطقه‌ای",
  [SmsType.Value4]: "پیامک بعد از ثبت نوبت",
};
export const allSmsTypes = [SmsType.Value0, SmsType.Value1, SmsType.Value2, SmsType.Value4];

export const templateStatus: Record<number, { label: string; tone: "approved" | "pending" | "rejected" }> = {
  [SmsTemplateStatus.Value0]: { label: "در انتظار بررسی", tone: "pending" },
  [SmsTemplateStatus.Value1]: { label: "تأییدشده", tone: "approved" },
  [SmsTemplateStatus.Value2]: { label: "ردشده", tone: "rejected" },
};

export const TEMPLATE_TITLE_MAX = 50;
export const TEMPLATE_TEXT_MAX = 1000;
export const GROUP_NAME_MAX = 100;

// The backend appends this opt-out line; the editor shows it as a locked footer instead of editable text.
export const OPT_OUT = "لغو11";
const optOutPattern = /\s*لغو\s?(11|۱۱)\s*$/;
export const stripOptOut = (text?: string | null) => (text ?? "").replace(optOutPattern, "");
export const withOptOut = (text: string) => `${text.trim()}\n${OPT_OUT}`;

export const faNumber = (value: number) => value.toLocaleString("fa-IR");

// Persian SMS is UCS-2: one message holds 70 characters, a multipart message 67 per part.
export function smsSegments(length: number) {
  if (length === 0) return { segments: 0, remaining: 70 };
  const segments = length <= 70 ? 1 : Math.ceil(length / 67);
  const limit = segments === 1 ? 70 : segments * 67;
  return { segments, remaining: limit - length };
}

const ordinals = ["اول", "دوم", "سوم", "چهارم", "پنجم", "ششم", "هفتم", "هشتم", "نهم", "دهم"];
export const nextSmsLabel = (segments: number) => ordinals[segments] ?? "بعدی";

const keyOf = (tag: PlaceHolder) => tag.key ?? "";

export type PlaceholderGroup = { name: string; note?: string; tags: PlaceHolder[] };
export function groupPlaceholders(tags: PlaceHolder[]): PlaceholderGroup[] {
  const groups: PlaceholderGroup[] = [
    { name: "مشتری", tags: [] },
    { name: "کسب‌وکار", tags: [] },
    { name: "تخفیف", tags: [] },
    { name: "نوبت", tags: [] },
  ];
  for (const tag of tags) {
    const name = tag.propertyName ?? keyOf(tag).replace(/[{}]/g, "");
    if (name === "FirstName" || name === "LastName") groups[0].tags.push(tag);
    else if (name.startsWith("Discount")) groups[2].tags.push(tag);
    else if (name.startsWith("Appointment")) groups[3].tags.push(tag);
    else groups[1].tags.push(tag);
  }
  // Booking fields only exist in the SMS sent after a booking.
  if (groups[3].tags.length) groups[3].note = "فقط در پیامک بعد از ثبت نوبت";
  const discount = groups[2];
  if (discount.tags.length && discount.tags.every((tag) => tag.applicableTypes?.length === 1 && tag.applicableTypes[0] === SmsType.Value1))
    discount.note = "فقط در پیامک تخفیف";
  else if (discount.tags.some((tag) => tag.applicableTypes?.length === 1)) discount.note = "بعضی فقط در پیامک تخفیف";
  return groups.filter((group) => group.tags.length);
}

export const profileLinkFor = (tag: PlaceHolder) =>
  /Instagram|Telegram|Whatsapp/i.test(tag.propertyName ?? keyOf(tag)) ? "/dashboard/profile/edit/socialMedia" : "/dashboard/profile/edit";

// Same rule as the backend's PlaceHolderDomainService.ApplicableFor: the types every used placeholder allows.
export function applicableTypes(text: string, tags: PlaceHolder[]) {
  const used = tags.filter((tag) => tag.key && text.includes(tag.key));
  return allSmsTypes.map((type) => {
    const blocker = used.find((tag) => !(tag.applicableTypes ?? allSmsTypes).includes(type));
    return { type, label: smsTypeLabels[type], allowed: !blocker, blocker: blocker?.value ?? "" };
  });
}

export type TextPart = { text: string } | { tag: PlaceHolder; key: string };
export function splitByPlaceholders(text: string, tags: PlaceHolder[]): TextPart[] {
  const parts: TextPart[] = [];
  const regex = /{{(.*?)}}/g;
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    const tag = tags.find((t) => t.key === match![0]);
    if (!tag) continue;
    if (match.index > last) parts.push({ text: text.slice(last, match.index) });
    parts.push({ tag, key: match[0] });
    last = regex.lastIndex;
  }
  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts;
}

// Fills placeholders with the merchant's real values (or the backend's sample customer) for the preview.
export function previewText(text: string, tags: PlaceHolder[]) {
  return splitByPlaceholders(text, tags)
    .map((part) => ("text" in part ? part.text : part.tag.preView || `[${part.tag.value}]`))
    .join("");
}

const toPersianDigits = (value: string) => value.replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]);
// 09123456789 → ۰۹۱۲ ۳۴۵ ۶۷۸۹, isolated as a left-to-right run so the groups keep their order in RTL text.
const LRI = String.fromCharCode(0x2066);
const PDI = String.fromCharCode(0x2069);
export function formatPhone(phone?: string | null) {
  const digits = (phone ?? "").replace(/\s/g, "");
  const grouped = /^0\d{10}$/.test(digits) ? `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}` : digits;
  return grouped ? LRI + toPersianDigits(grouped) + PDI : "";
}
export const customerName = (customer: { firstName?: string | null; lastName?: string | null }) =>
  `${customer.firstName ?? ""} ${customer.lastName ?? ""}`.trim();

const dayMonth = new Intl.DateTimeFormat("fa-IR", { day: "numeric", month: "long" });
export function createdLabel(iso?: string) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const today = new Date();
  return date.toDateString() === today.toDateString() ? "ساخته‌شده امروز" : `ساخته‌شده در ${dayMonth.format(date)}`;
}
